import ImageWithFallback from '@components/image/ImageWithFallback'
import Button, { IconButton, LinkButton } from '@components/shared/Button'
import Modal from '@components/shared/Modal'
import { TOKEN_WATCHLIST_KEY } from '@components/watchlist/WatchlistButton'
import { ArrowsPointingOutIcon, TrashIcon } from '@heroicons/react/20/solid'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import { ModalProps } from 'types/modal'
import { Responsive, WidthProvider, Layout } from 'react-grid-layout'
import { useTranslation } from 'next-i18next'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'

const ResponsiveGridLayout = WidthProvider(Responsive)

const EditWatchlistModal = ({ isOpen, onClose }: ModalProps) => {
  const { t } = useTranslation('watchlist')
  const [watchlist, setWatchlist] = useLocalStorage(TOKEN_WATCHLIST_KEY)

  const handleLayoutChange = (layout: Layout[]) => {
    // create a map of mint to token for quick lookup
    const tokenMap = new Map(watchlist.map((token) => [token.mint, token]))

    // sort items based on their new position in the grid and map back to tokens
    const newOrder = layout
      .sort((a, b) => a.y - b.y || a.x - b.x)
      .map((item) => tokenMap.get(item.i))
      .filter(Boolean)

    setWatchlist(newOrder)
  }

  const layouts = {
    lg: watchlist.map((token) => ({
      i: token.mint, // use mint address as the unique id
      x: 0,
      y: watchlist.indexOf(token),
      w: 1,
      h: 1,
      static: false,
    })),
  }

  const handleRemoveAll = () => {
    setWatchlist([])
    onClose()
  }

  const handleDelete = (mint: string) => {
    setWatchlist(watchlist.filter((t) => t.mint !== mint))
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <h2 className="mb-4 text-center">{t('edit-watchlist')}</h2>
      <p className="mb-1 text-xs">{t('drag-to-reorder')}</p>
      <div className="thin-scroll overflow-y-auto overflow-x-hidden sm:max-h-96">
        <ResponsiveGridLayout
          className="layout"
          layouts={layouts}
          breakpoints={{ lg: 1200 }}
          cols={{ lg: 1 }}
          rowHeight={50}
          margin={[0, 8]}
          onLayoutChange={handleLayoutChange}
          isDraggable
          isResizable={false}
          draggableHandle=".drag-handle"
        >
          {watchlist.map((token) => (
            <div
              key={token.mint}
              className="flex items-center justify-between rounded-lg border border-th-bkg-3 bg-th-bkg-1 px-4 py-2"
            >
              <div className="flex items-center gap-2">
                <IconButton
                  className="drag-handle mr-2 cursor-move text-th-fgd-3"
                  hideBg
                >
                  <ArrowsPointingOutIcon className="size-4" />
                </IconButton>
                {token?.image_url ? (
                  <ImageWithFallback
                    src={token.image_url}
                    alt={token.symbol}
                    width={20}
                    height={20}
                  />
                ) : null}
                <p className="text-th-fgd-1">{token.symbol}</p>
              </div>
              <IconButton
                className="text-th-fgd-3"
                onClick={() => handleDelete(token.mint)}
                hideBg
              >
                <TrashIcon className="size-4" />
              </IconButton>
            </div>
          ))}
        </ResponsiveGridLayout>
      </div>
      <div className="flex flex-col items-center">
        <Button className="mt-4 w-full" onClick={onClose}>
          {t('save')}
        </Button>
        <LinkButton className="mt-4 font-semibold" onClick={handleRemoveAll}>
          {t('remove-all')}
        </LinkButton>
      </div>
    </Modal>
  )
}

export default EditWatchlistModal
